import { S3UploadError } from '../apis/asset-upload/s3-upload-error';
import { FILE_METADATA_ERROR_MESSAGES } from './file-error-messages';
import { isProjectPlanLimitExceededError } from './project-plan-limit';

/**
 * 업로드 실패 사유 분류.
 *
 * - NETWORK / TIMEOUT / FORBIDDEN: S3 업로드 단계의 네트워크·타임아웃·권한 만료 (`S3UploadError.reason` 기반)
 * - UNSUPPORTED_FORMAT: 브라우저가 비디오 컨테이너/코덱을 열지 못함 (Code 4 + DEMUXER_ERROR 등)
 * - DECODE_FAILED: 비디오 디코딩 단계 실패 (Code 3). 코덱 미지원(HEVC 등) 또는 파일 손상 두 가능성 모두 포함
 * - DURATION_UNRESOLVED: 비디오 길이 읽기 실패
 * - AUDIO_OPEN_FAILED: 오디오 메타데이터/길이 로드 실패
 * - NOT_VIDEO: 비디오 업로드 흐름인데 파일이 비디오가 아님
 * - NOT_AUDIO: 오디오 업로드 흐름인데 파일이 오디오가 아님
 * - PLAN_LIMIT_EXCEEDED: 현재 플랜의 수치 제한(영상 길이/용량 등)을 초과함
 * - PREFLIGHT_*: 대용량 브라우저 S3 probe 단계에서 업로드 시작 전 실패
 * - UNKNOWN: 위 어느 분기에도 매칭되지 않음
 */
export type UploadFailureReason =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'FORBIDDEN'
  | 'UNSUPPORTED_FORMAT'
  | 'DECODE_FAILED'
  | 'DURATION_UNRESOLVED'
  | 'AUDIO_OPEN_FAILED'
  | 'NOT_VIDEO'
  | 'NOT_AUDIO'
  | 'PLAN_LIMIT_EXCEEDED'
  | 'PREFLIGHT_OFFLINE'
  | 'PREFLIGHT_TIMEOUT'
  | 'PREFLIGHT_NETWORK_ERROR'
  | 'PREFLIGHT_NETWORK_RESTRICTED'
  | 'PREFLIGHT_FORBIDDEN'
  | 'PREFLIGHT_CORS_OR_ETAG'
  | 'PREFLIGHT_UNKNOWN'
  | 'UNKNOWN';

export interface UploadFailureClassification {
  reason: UploadFailureReason;
  /** next-intl 키. 호출자가 `t(classification.i18nKey, classification.i18nValues)` 로 사용. */
  i18nKey: string;
  i18nValues: {
    name: string;
    message?: string;
    startSeconds?: string;
    endSeconds?: string;
  };
  /** 디버깅·로깅용 raw 정보. 사용자에게 노출하지 않는다. */
  raw?: {
    code?: number | string;
    httpStatus?: number;
    message?: string;
  };
}

export const I18N_KEY_BY_REASON: Record<UploadFailureReason, string> = {
  NETWORK: 'Error.client-upload-network',
  TIMEOUT: 'Error.client-upload-timeout',
  FORBIDDEN: 'Error.client-upload-forbidden',
  UNSUPPORTED_FORMAT: 'Error.client-upload-unsupported-format',
  DECODE_FAILED: 'Error.client-upload-decode-failed',
  DURATION_UNRESOLVED: 'Error.client-upload-duration-unresolved',
  AUDIO_OPEN_FAILED: 'Error.client-upload-audio-open-failed',
  NOT_VIDEO: 'Error.client-upload-not-video',
  NOT_AUDIO: 'Error.client-upload-not-audio',
  PLAN_LIMIT_EXCEEDED: 'Error.plan-limit-exceeded',
  PREFLIGHT_OFFLINE: 'Error.upload-preflight-offline',
  PREFLIGHT_TIMEOUT: 'Error.upload-preflight-unstable',
  PREFLIGHT_NETWORK_ERROR: 'Error.upload-preflight-unstable',
  PREFLIGHT_NETWORK_RESTRICTED: 'Error.upload-preflight-network-restricted',
  PREFLIGHT_FORBIDDEN: 'Error.upload-preflight-service',
  PREFLIGHT_CORS_OR_ETAG: 'Error.upload-preflight-service',
  PREFLIGHT_UNKNOWN: 'Error.upload-preflight-unknown',
  UNKNOWN: 'Error.file-upload-failed',
};

const INVALID_FEATURE_AUTH_CODE = 1500;
const DEFAULT_PLAN_LIMIT_MESSAGE = '현재 플랜 제한을 초과했어요.';

const VIDEO_LOAD_PREFIX =
  FILE_METADATA_ERROR_MESSAGES.VIDEO_LOAD_PREFIX.replace(/\./g, '\\.');
const VIDEO_LOAD_ERROR_PATTERN = new RegExp(
  `^${VIDEO_LOAD_PREFIX}\\s*Code:\\s*(\\d+|undefined)(?:,\\s*Message:\\s*([\\s\\S]*))?$`,
);
const UNSUPPORTED_FORMAT_HINTS =
  /DEMUXER_ERROR_COULD_NOT_OPEN|unsupported|NOT_SUPPORTED/i;
const UNSUPPORTED_FILE_EXTENSION_PATTERN = /^Unsupported file extension:/i;

const build = (
  reason: UploadFailureReason,
  fileName: string,
  raw?: UploadFailureClassification['raw'],
  i18nValues?: Omit<UploadFailureClassification['i18nValues'], 'name'>,
): UploadFailureClassification => ({
  reason,
  i18nKey: I18N_KEY_BY_REASON[reason],
  i18nValues: { name: fileName, ...i18nValues },
  raw,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const extractHttpError = (
  error: unknown,
): UploadFailureClassification['raw'] | null => {
  if (!isRecord(error) || !isRecord(error.response)) return null;

  const response = error.response;
  const data = isRecord(response.data) ? response.data : {};
  const status = response.status;
  const code = data.code;
  const message = data.message;

  return {
    httpStatus: typeof status === 'number' ? status : undefined,
    code:
      typeof code === 'number' || typeof code === 'string' ? code : undefined,
    message: typeof message === 'string' ? message : undefined,
  };
};

/**
 * 임의의 에러를 사용자 안내 메시지로 매핑할 수 있는 분류 결과로 변환한다.
 *
 * 사용 예 (호출자):
 * ```
 * try {
 *   await uploadFile(...);
 * } catch (error) {
 *   const classification = classifyUploadFailure(error, file.name);
 *   toast({
 *     type: 'error',
 *     description: t(classification.i18nKey, classification.i18nValues),
 *     toastOptions: { position: 'top-center' },
 *   });
 * }
 * ```
 */
export const classifyUploadFailure = (
  error: unknown,
  fileName: string,
): UploadFailureClassification => {
  if (isProjectPlanLimitExceededError(error)) {
    return build(
      'PLAN_LIMIT_EXCEEDED',
      fileName,
      { message: error.message },
      { message: error.message || DEFAULT_PLAN_LIMIT_MESSAGE },
    );
  }

  const httpError = extractHttpError(error);
  if (String(httpError?.code) === String(INVALID_FEATURE_AUTH_CODE)) {
    const planLimitMessage =
      httpError?.message?.trim() || DEFAULT_PLAN_LIMIT_MESSAGE;
    return build(
      'PLAN_LIMIT_EXCEEDED',
      fileName,
      httpError ?? { code: INVALID_FEATURE_AUTH_CODE },
      { message: planLimitMessage },
    );
  }

  if (error instanceof S3UploadError) {
    switch (error.reason) {
      case 'NETWORK':
        return build('NETWORK', fileName, { message: error.message });
      case 'TIMEOUT':
        return build('TIMEOUT', fileName, { message: error.message });
      case 'FORBIDDEN':
        return build('FORBIDDEN', fileName, {
          httpStatus: 403,
          message: error.message,
        });
      default:
        return build('UNKNOWN', fileName, { message: error.message });
    }
  }

  const message = error instanceof Error ? error.message : '';

  if (UNSUPPORTED_FILE_EXTENSION_PATTERN.test(message)) {
    return build('UNSUPPORTED_FORMAT', fileName, { message });
  }

  const videoLoadMatch = message.match(VIDEO_LOAD_ERROR_PATTERN);
  if (videoLoadMatch) {
    const codeStr = videoLoadMatch[1];
    const code = codeStr === 'undefined' ? undefined : Number(codeStr);
    const detail = videoLoadMatch[2] ?? '';

    if (code === 4 || UNSUPPORTED_FORMAT_HINTS.test(detail)) {
      return build('UNSUPPORTED_FORMAT', fileName, { code, message: detail });
    }
    if (code === 3) {
      // Code 3 (MEDIA_ERR_DECODE) — Chromium 의 DECODER_ERROR_NOT_SUPPORTED (HEVC 코덱 미지원 등)
      // 또는 실제 파일 손상 두 가능성 모두 포함. 카피에서 두 가지 안내.
      return build('DECODE_FAILED', fileName, { code, message: detail });
    }
    // Code 1 (ABORTED), Code 2 (NETWORK from blob), undefined (video.error === null) 등.
    // 구체적 사유를 단정할 수 없으므로 UNKNOWN 으로 fallback (오안내 회피).
    return build('UNKNOWN', fileName, { code, message: detail });
  }

  if (message === FILE_METADATA_ERROR_MESSAGES.VIDEO_DURATION_UNRESOLVED) {
    return build('DURATION_UNRESOLVED', fileName);
  }
  if (
    message === FILE_METADATA_ERROR_MESSAGES.AUDIO_LOAD_FAILED ||
    message === FILE_METADATA_ERROR_MESSAGES.AUDIO_DURATION_UNRESOLVED
  ) {
    return build('AUDIO_OPEN_FAILED', fileName);
  }
  if (message === FILE_METADATA_ERROR_MESSAGES.NOT_VIDEO) {
    return build('NOT_VIDEO', fileName);
  }
  if (message === FILE_METADATA_ERROR_MESSAGES.NOT_AUDIO) {
    return build('NOT_AUDIO', fileName);
  }

  return build('UNKNOWN', fileName, {
    message: message || undefined,
  });
};
